import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { CATEGORIES } from '../../pages/challenges/categories.js';

const DRAG_TYPE = 'CAROUSEL_ITEM';

// ---------------------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------------------
function EditModal({ image, show, onHide, onSave, onDelete }) {
  const [type, setType] = useState(image.CarouselItem.Type);
  const [active, setActive] = useState(image.CarouselItem.Active);
  const [category, setCategory] = useState(
    image.CarouselItem.Type === 'category' ? image.CarouselItem.Id : CATEGORIES[0],
  );
  const challengeIdRef = useRef(null);

  const handleSave = () => {
    onSave({
      ...image,
      CarouselItem: {
        ...image.CarouselItem,
        Active: active,
        Type: type,
        Id: type === 'challenge' ? challengeIdRef.current?.value ?? '' : category,
      },
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="text-base font-semibold">Edit carousel item</Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {/* Image preview */}
        <div className="mb-4 overflow-hidden rounded-xl" style={{ aspectRatio: '16/7' }}>
          <img
            src={image.src}
            alt="carousel item"
            className="h-full w-full object-cover"
          />
        </div>

        {/* Type */}
        <Form.Group className="mb-4">
          <Form.Label className="text-sm fw-semibold text-ink-muted mb-2">Link type</Form.Label>
          <div className="d-flex gap-2">
            {['challenge', 'category'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={[
                  'px-4 py-2 rounded-lg text-sm font-medium border transition-all',
                  type === t
                    ? 'bg-brand text-white border-brand'
                    : 'bg-white text-ink-muted border-slate-200 hover:border-brand hover:text-brand',
                ].join(' ')}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </Form.Group>

        {/* Challenge ID */}
        {type === 'challenge' && (
          <Form.Group className="mb-4">
            <Form.Label className="text-sm fw-semibold text-ink-muted">Challenge ID</Form.Label>
            <Form.Control
              ref={challengeIdRef}
              type="text"
              placeholder="e.g. abc123xyz"
              defaultValue={
                image.CarouselItem.Type === 'challenge' ? image.CarouselItem.Id : ''
              }
              className="mt-1"
            />
          </Form.Group>
        )}

        {/* Category */}
        {type === 'category' && (
          <Form.Group className="mb-4">
            <Form.Label className="text-sm fw-semibold text-ink-muted">Category</Form.Label>
            <Form.Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* Active */}
        <Form.Group className="d-flex align-items-center gap-3 py-3 border-top border-slate-100">
          <div className="flex-grow-1">
            <p className="mb-0 text-sm fw-semibold">Active</p>
            <p className="mb-0 text-xs text-ink-muted">Show this item in the app carousel</p>
          </div>
          <Form.Check
            type="switch"
            id="modal-active-switch"
            checked={active}
            onChange={() => setActive((v) => !v)}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex justify-content-between">
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => { onDelete(image); onHide(); }}
        >
          Delete
        </Button>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Save changes
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Individual card
// ---------------------------------------------------------------------------
function CarouselCard({ image, index, imageCount, moveImage, onUpdate, onDelete }) {
  const dropRef = useRef(null);
  const dragRef = useRef(null);
  const [showEdit, setShowEdit] = useState(false);

  // Outer div is the drop target so the whole card area can receive drops
  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    hover(item) {
      if (!dropRef.current || item.index === index) return;
      moveImage(item.index, index);
      item.index = index;
    },
  });

  // Only the image section is the drag handle
  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: { id: image.id, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drop(dropRef);
  drag(dragRef);

  const { Type, Id, Active } = image.CarouselItem;
  const label = Type === 'category' ? Id : Id ? Id.slice(0, 12) + (Id.length > 12 ? '…' : '') : '';
  const isConfigured = Id && Id.length > 0;

  return (
    <>
      <div
        ref={dropRef}
        style={{ opacity: isDragging ? 0.35 : 1 }}
        className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft transition-all hover:shadow-md"
      >
        {/* Image — this is the drag handle */}
        <div
          ref={dragRef}
          style={{ aspectRatio: '16/9' }}
          className="group/img relative overflow-hidden bg-slate-100 cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <img
            src={image.src}
            alt={`carousel ${index + 1}`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover/img:scale-105 select-none pointer-events-none"
          />

          {/* Drag hint overlay — subtle, only on hover */}
          <div className="absolute inset-0 flex items-end justify-center pb-2 opacity-0 group-hover/img:opacity-100 transition-opacity pointer-events-none">
            <span className="flex items-center gap-1 rounded-full bg-ink/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
              <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
              </svg>
              Drag to reorder
            </span>
          </div>

          {/* Index badge */}
          <span className="absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-ink/60 text-white text-xs font-bold select-none pointer-events-none">
            {index + 1}
          </span>

          {/* Active dot */}
          <span
            title={Active ? 'Active' : 'Inactive'}
            className={`absolute top-2 right-2 h-2.5 w-2.5 rounded-full border-2 border-white ${Active ? 'bg-brand' : 'bg-slate-400'}`}
          />
        </div>

        {/* Bottom action strip — always visible, not draggable */}
        <div className="flex items-center gap-2 px-2 py-2 border-t border-slate-100">
          {/* Type + label */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${Type === 'category' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>
              {Type}
            </span>
            <span className={`truncate text-xs ${isConfigured ? 'text-ink-muted' : 'text-red-500 font-medium'}`}>
              {isConfigured ? label : 'Not configured'}
            </span>
          </div>

          {/* Reorder buttons */}
          <button
            type="button"
            disabled={index === 0}
            onClick={() => moveImage(index, index - 1)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move left"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            type="button"
            disabled={index === imageCount - 1}
            onClick={() => moveImage(index, index + 1)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move right"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>

          {/* Edit button */}
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="flex h-7 shrink-0 items-center gap-1 rounded-md bg-slate-100 px-2 text-xs font-medium text-ink hover:bg-brand hover:text-white transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487a2.25 2.25 0 113.182 3.182L7.5 20.213l-4.182.546.546-4.182L16.862 4.487z" />
            </svg>
            Edit
          </button>
        </div>
      </div>

      {showEdit && (
        <EditModal
          image={image}
          show={showEdit}
          onHide={() => setShowEdit(false)}
          onSave={onUpdate}
          onDelete={onDelete}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------
export default function CarouselDnD({ images, callback, moveImage, onDelete }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
      {images.map((image, index) => (
        <CarouselCard
          key={image.id}
          image={image}
          index={index}
          imageCount={images.length}
          moveImage={moveImage}
          onUpdate={callback}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
